import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { TextField } from "./field";

const SEARCH_DEBOUNCE_MS = 300;

interface SearchInputProps {
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}

/**
 * Debounced so typing does not fire a request per keystroke, while the field
 * itself stays fully controlled and responsive.
 *
 * Used by every list screen, all of which keep their filters in the URL.
 */
export function SearchInput({ value, placeholder, onChange }: SearchInputProps) {
  const { t } = useTranslation("common");
  const [draft, setDraft] = useState(value);
  const [appliedValue, setAppliedValue] = useState(value);

  // The URL is the source of truth, so a filter cleared elsewhere has to show
  // up here. Adjusted during render instead of in an effect to avoid the
  // extra pass that would briefly display the stale text.
  if (appliedValue !== value) {
    setAppliedValue(value);
    setDraft(value);
  }

  useEffect(() => {
    if (draft === value) {
      return;
    }

    const timer = setTimeout(() => {
      onChange(draft);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [draft, onChange, value]);

  return (
    <TextField
      label={t("search")}
      type="search"
      placeholder={placeholder}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
    />
  );
}
